import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import jamespot, {
    Format,
    WidgetDatasourceValues,
    WidgetDisplayMode,
    WidgetExcelDatasourceTableContent,
    WidgetsName,
    WidgetTableColumn,
} from 'jamespot-user-api';
import styled from 'styled-components';
import { Widget, WidgetEditor } from 'jamespot-front-business';
import { getVisibleColumns } from './utils';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch } from '../../../redux/store';

const JRCButton = commonComponentsRegistry.getLazyComponent('Button');

const WidgetTable = commonComponentsRegistry.getLazyComponent('WidgetTable');
const Loader = commonComponentsRegistry.getLazyComponent('Loader');

export const Wrapper = styled.div``;

export const AbsoluteWrapper = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${(props) => props.theme.space.md}px;
`;

export const WidgetExcelDatasourceTableWrapper = ({
    uri,
    limit,
    tableHeadColor,
    tableHeadTextColor,
    tableHeadIconColor,
    tableRowColor,
    tableRowTextColor,
    tableColumnsData = [],
    tableBorderRadius,
    tableSizedColumns,
    tableSizedColumnsWidth,
    mode,
}: WidgetExcelDatasourceTableContent & { uniqid: string; mode: WidgetDisplayMode }) => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [datasourceData, setDatasourceData] = useState<Record<string, WidgetDatasourceValues>[]>([]);
    const [datasourceModel, setDatasourceModel] = useState<WidgetTableColumn[]>([]);

    const definition = useMemo(() => Widget.definition(WidgetsName.ExcelDatasourceTable), []);

    useEffect(() => {
        if (uri !== undefined && tableColumnsData.length !== 0 && datasourceModel.length === 0) {
            setIsLoading(true);
            jamespot.datasource
                .get(uri, Format.VIEW)
                .then((response) => {
                    setDatasourceModel(response.result.model);
                    setDatasourceData(response.result.data);
                })
                .finally(() => setIsLoading(false));
        }
    }, [uri, tableColumnsData, datasourceModel]);

    useEffect(() => {
        if (!uri) {
            setDatasourceModel([]);
        }
    }, [uri]);

    useEffect(() => {
        if (mode === 'view' || mode === 'preview') {
            dispatch(WidgetEditor.slice.actions.flushAllEditor());
        }
    }, [mode, dispatch]);

    const columns = useMemo(
        () => getVisibleColumns(tableColumnsData, datasourceModel),
        [tableColumnsData, datasourceModel],
    );

    if (tableColumnsData.length === 0 && !isLoading && mode === 'edit') {
        return (
            <>
                <AbsoluteWrapper>
                    <img src={`/img/fast-intranet/${WidgetsName.ExcelDatasourceTable}.png`} alt="" />
                    <FormattedMessage id={definition.label} />
                    <JRCButton label="GLOBAL_Settings" />
                </AbsoluteWrapper>
            </>
        );
    }

    return isLoading ? (
        <AbsoluteWrapper>
            <Loader />
        </AbsoluteWrapper>
    ) : (
        <>
            {!isLoading && datasourceData.length === 0 && mode !== 'edit' ? (
                <AbsoluteWrapper>
                    <img src={`/img/fast-intranet/${WidgetsName.ExcelDatasourceTable}.png`} alt="" />
                    <FormattedMessage id={'WIDGET_Datasource_No_Results'} />
                </AbsoluteWrapper>
            ) : (
                <WidgetTable
                    rows={datasourceData}
                    columns={columns}
                    limit={limit}
                    tableHeadColor={tableHeadColor}
                    tableHeadTextColor={tableHeadTextColor}
                    tableHeadIconColor={tableHeadIconColor}
                    tableRowColor={tableRowColor}
                    tableRowTextColor={tableRowTextColor}
                    tableBorderRadius={tableBorderRadius}
                    tableColumnsData={tableColumnsData}
                    tableSizedColumns={tableSizedColumns}
                    tableSizedColumnsWidth={tableSizedColumnsWidth}
                />
            )}
        </>
    );
};
